import React, { useEffect } from 'react';

/**
 * UndoToast (§4, erratum #3 — REQUIRED, not optional). After an OPTIMISTIC attribute write the sheet
 * dismisses immediately and the pill updates instantly; this toast then rides above the bottom nav for 5s
 * offering UNDO. On failure the caller reverts the pill and swaps this to an error message. Without a
 * visible undo, a mis-tap becomes silent data loss on a touch surface — hence non-optional.
 *
 * Sits above BottomBar (fixed bottom-[72px], matching VersionFooter's mobile lift) so it never hides
 * behind the mobile nav; on md+ there is no BottomBar so it drops to the bottom edge.
 */
export interface UndoToastProps {
  message: string;
  variant?: 'undo' | 'error';
  onUndo?: () => void;
  onExpire: () => void;
  durationMs?: number; // default 5000
  /** Overrides the action button's label (default "Undo"). Lets an error-variant toast carry a
   *  non-undo action — e.g. "Refresh" on a write-landed-but-unconfirmed outcome, where the right
   *  affordance RE-CHECKS the durable state rather than re-submitting the write. */
  actionLabel?: string;
}

export const UndoToast: React.FC<UndoToastProps> = ({ message, variant = 'undo', onUndo, onExpire, durationMs = 5000, actionLabel }) => {
  useEffect(() => {
    const t = setTimeout(onExpire, durationMs);
    return () => clearTimeout(t);
  }, [onExpire, durationMs]);

  return (
    <div role="status" aria-live="polite"
      className="fixed inset-x-0 z-[80] flex justify-center bottom-[72px] md:bottom-3 pointer-events-none px-4">
      <div className={`pointer-events-auto flex items-center gap-3 max-w-md w-full md:w-auto ps-4 pe-2 py-2 rounded-xl shadow-lg text-sm text-white ${variant === 'error' ? 'bg-error' : 'bg-slate-nav'}`}>
        <span className="flex-1 truncate">{message}</span>
        {onUndo && (
          <button type="button" onClick={onUndo} data-testid="toast-action"
            className="shrink-0 h-8 px-3 rounded-lg font-semibold bg-white/15 hover:bg-white/25">{actionLabel ?? 'Undo'}</button>
        )}
      </div>
    </div>
  );
};
